/**
 * AuthProvider (CONTRACTS.md §2).
 *
 * - Real blocking initialization state: status stays 'loading' until the
 *   persisted session (if any) AND the server-side profile row are resolved.
 * - Profile bootstrap goes through repositories.profile (workstream D
 *   implements the persistence layer behind this contract).
 * - Role is read from the server-side profile row only.
 * - When Supabase is not configured the provider resolves to
 *   'unauthenticated'; auth pages render an honest configuration Banner.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import type { Session } from '@supabase/supabase-js'
import type { Profile } from '@/types'
import { getSupabase, isCloudConfigured } from '@/lib/supabase/client'
import { repositories } from '@/services/repositories'
import { PATHS } from '@/app/router/paths'
import {
  notConfiguredError,
  profileUnavailableError,
  roleMismatchError,
  toAuthError,
} from './authErrors'
import { validateDisplayName, validateEmail, validatePassword } from './validation'
import { makeAppError, newCorrelationId } from '@/lib/supabase/errors'
import {
  AuthContext,
  type AuthContextValue,
  type PortalRole,
  type SignInInput,
  type SignUpInput,
} from './authContext'

type AuthState = {
  status: 'loading' | 'authenticated' | 'unauthenticated'
  userId: string | null
  email: string | null
  profile: Profile | null
}

const INITIAL_STATE: AuthState = { status: 'loading', userId: null, email: null, profile: null }

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms))
}

/**
 * Reads the profile row. Right after sign-up the database trigger may need a
 * moment, so a null result is retried briefly. Throws are NOT retried here —
 * callers decide how to surface them.
 */
async function loadProfileWithRetry(attempts = 3, delayMs = 600): Promise<Profile | null> {
  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    const profile = await repositories.profile.getMyProfile()
    if (profile) return profile
    if (attempt < attempts) await wait(delayMs)
  }
  return null
}

function validationError(message: string) {
  return makeAppError({
    code: 'VALIDATION_ERROR',
    userMessage: message,
    retryable: false,
    source: 'validation',
    correlationId: newCorrelationId(),
  })
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(INITIAL_STATE)
  // Mirror of identity facts for the auth-event callback (avoids stale closures).
  const identityRef = useRef<{ userId: string | null; hasProfile: boolean }>({
    userId: null,
    hasProfile: false,
  })

  const applyState = useCallback((next: AuthState) => {
    identityRef.current = { userId: next.userId, hasProfile: next.profile !== null }
    setState(next)
  }, [])

  const bootstrapFromSession = useCallback(
    async (session: Session): Promise<AuthState> => {
      const profile = await loadProfileWithRetry()
      if (!profile) throw profileUnavailableError()
      return {
        status: 'authenticated',
        userId: session.user.id,
        email: session.user.email ?? null,
        profile,
      }
    },
    [],
  )

  // Blocking session restoration + auth event subscription.
  useEffect(() => {
    const supabase = getSupabase()
    if (!supabase || !isCloudConfigured()) {
      applyState({ status: 'unauthenticated', userId: null, email: null, profile: null })
      return
    }

    let active = true

    const restore = async () => {
      try {
        const { data, error } = await supabase.auth.getSession()
        if (error) throw toAuthError(error)
        if (!active) return
        if (!data.session) {
          applyState({ status: 'unauthenticated', userId: null, email: null, profile: null })
          return
        }
        const next = await bootstrapFromSession(data.session)
        if (active) applyState(next)
      } catch {
        // A restore failure must never strand the user in 'loading' or smuggle
        // them in without a verified profile: sign out and land unauthenticated.
        try {
          await supabase.auth.signOut()
        } catch {
          // best effort — the local state below is the honest part
        }
        if (active) applyState({ status: 'unauthenticated', userId: null, email: null, profile: null })
      }
    }
    void restore()

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // Defer async work out of Supabase's internal callback (their guidance:
      // no await on supabase calls inside the handler).
      window.setTimeout(() => {
        if (!active) return
        if (event === 'SIGNED_OUT' || !session) {
          if (event === 'SIGNED_OUT') {
            applyState({ status: 'unauthenticated', userId: null, email: null, profile: null })
          }
          return
        }
        if (event === 'SIGNED_IN' || event === 'USER_UPDATED' || event === 'PASSWORD_RECOVERY') {
          const known = identityRef.current
          if (known.userId === session.user.id && known.hasProfile) return
          bootstrapFromSession(session)
            .then((next) => {
              if (active) applyState(next)
            })
            .catch(() => {
              // Keep current state; explicit sign-in flows surface their own errors.
            })
          return
        }
        if (event === 'TOKEN_REFRESHED') {
          // Session is still valid; nothing to re-bootstrap.
          return
        }
      }, 0)
    })

    return () => {
      active = false
      subscription.unsubscribe()
    }
  }, [applyState, bootstrapFromSession])

  const signIn = useCallback(
    async (input: SignInInput) => {
      const supabase = getSupabase()
      if (!supabase) throw notConfiguredError()

      const emailError = validateEmail(input.email)
      if (emailError) throw validationError(emailError)
      if (input.password.length === 0) throw validationError('Vul je wachtwoord in.')

      const { data, error } = await supabase.auth.signInWithPassword({
        email: input.email.trim(),
        password: input.password,
      })
      if (error) throw toAuthError(error)
      if (!data.session) {
        throw toAuthError(new Error('sign-in completed without a session'))
      }

      let next: AuthState
      try {
        next = await bootstrapFromSession(data.session)
      } catch (profileError) {
        await supabase.auth.signOut().catch(() => undefined)
        throw profileError
      }

      // Role enforcement happens here, against the server-side profile row.
      if (next.profile && next.profile.role !== input.role) {
        await supabase.auth.signOut().catch(() => undefined)
        applyState({ status: 'unauthenticated', userId: null, email: null, profile: null })
        throw roleMismatchError(input.role)
      }

      // Only after auth + profile + role checks succeed may the app open.
      applyState(next)
    },
    [applyState, bootstrapFromSession],
  )

  const signUp = useCallback(
    async (input: SignUpInput): Promise<{ needsEmailConfirmation: boolean }> => {
      const supabase = getSupabase()
      if (!supabase) throw notConfiguredError()

      const nameError = validateDisplayName(input.displayName)
      if (nameError) throw validationError(nameError)
      const emailError = validateEmail(input.email)
      if (emailError) throw validationError(emailError)
      const passwordError = validatePassword(input.password)
      if (passwordError) throw validationError(passwordError)
      if (input.role !== 'client' && input.role !== 'caregiver') {
        throw validationError('Kies een geldige rol.')
      }

      const { data, error } = await supabase.auth.signUp({
        email: input.email.trim(),
        password: input.password,
        options: {
          // The database trigger turns this metadata into the profile row;
          // the role claim is enforced server-side by RLS.
          data: { display_name: input.displayName.trim(), role: input.role },
          emailRedirectTo: `${window.location.origin}${PATHS.auth.callback}`,
        },
      })
      if (error) throw toAuthError(error)

      // Supabase returns an existing user with empty identities when the
      // address is already registered (anti-enumeration behavior). Surface it.
      if (data.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
        throw toAuthError(new Error('User already registered'))
      }

      if (!data.session) {
        // Email confirmation required: no session, no app entry.
        return { needsEmailConfirmation: true }
      }

      try {
        const next = await bootstrapFromSession(data.session)
        // The server-side profile row is authoritative: if the trigger did
        // not store the requested role, do not enter the app.
        if (next.profile && next.profile.role !== input.role) {
          await supabase.auth.signOut().catch(() => undefined)
          applyState({ status: 'unauthenticated', userId: null, email: null, profile: null })
          throw roleMismatchError(input.role)
        }
        applyState(next)
      } catch (profileError) {
        await supabase.auth.signOut().catch(() => undefined)
        throw profileError
      }
      return { needsEmailConfirmation: false }
    },
    [applyState, bootstrapFromSession],
  )

  const signOut = useCallback(async () => {
    const supabase = getSupabase()
    if (supabase) {
      const { error } = await supabase.auth.signOut()
      if (error) throw toAuthError(error)
    }
    applyState({ status: 'unauthenticated', userId: null, email: null, profile: null })
  }, [applyState])

  const sendPasswordReset = useCallback(async (email: string) => {
    const supabase = getSupabase()
    if (!supabase) throw notConfiguredError()
    const emailError = validateEmail(email)
    if (emailError) throw validationError(emailError)
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}${PATHS.auth.resetPassword}`,
    })
    if (error) throw toAuthError(error)
  }, [])

  const updatePassword = useCallback(async (newPassword: string) => {
    const supabase = getSupabase()
    if (!supabase) throw notConfiguredError()
    const passwordError = validatePassword(newPassword)
    if (passwordError) throw validationError(passwordError)
    const { error } = await supabase.auth.updateUser({ password: newPassword })
    if (error) throw toAuthError(error)
  }, [])

  const refreshProfile = useCallback(async () => {
    if (!identityRef.current.userId) return
    const profile = await repositories.profile.getMyProfile()
    setState((prev) => {
      const next = { ...prev, profile }
      identityRef.current = { userId: next.userId, hasProfile: profile !== null }
      return next
    })
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({
      status: state.status,
      userId: state.userId,
      email: state.email,
      profile: state.profile,
      role: state.profile?.role ?? null,
      signIn,
      signUp,
      signOut,
      sendPasswordReset,
      updatePassword,
      refreshProfile,
    }),
    [state, signIn, signUp, signOut, sendPasswordReset, updatePassword, refreshProfile],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export type { PortalRole, SignInInput, SignUpInput }
